import React, { useState, useEffect, useCallback } from 'react';
import { getRandomQuestions } from './data/questions';
import QuizScreen from './components/QuizScreen';
import SummaryScreen from './components/SummaryScreen';
import StartScreen from './components/StartScreen';
import './App.css';

function App() {
  const [gameState, setGameState] = useState('start'); // 'start', 'playing', 'summary'
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(7);
  const [isAnswered, setIsAnswered] = useState(false);

  // Start a new game
  const startGame = () => {
    const randomQuestions = getRandomQuestions();
    setQuestions(randomQuestions);
    setCurrentQuestionIndex(0);
    setUserAnswers([]);
    setScore(0);
    setTimeLeft(5);
    setIsAnswered(false);
    setGameState('playing');
  };

  // Handle answer selection
  const handleAnswer = useCallback((selectedAnswer) => {
    if (isAnswered) return;

    const currentQuestion = questions[currentQuestionIndex];
    const isCorrect = selectedAnswer === currentQuestion.correctAnswer;
    
    setIsAnswered(true);
    
    const newAnswer = {
      questionId: currentQuestion.id,
      selectedAnswer,
      correctAnswer: currentQuestion.correctAnswer,
      isCorrect,
      question: currentQuestion.question,
      timeRemaining: timeLeft
    };

    setUserAnswers(prev => [...prev, newAnswer]);
    
    if (isCorrect) {
      setScore(prev => prev + 1);
    }

    // Move to next question after a short delay
    setTimeout(() => {
      if (currentQuestionIndex + 1 < questions.length) {
        setCurrentQuestionIndex(prev => prev + 1);
        setTimeLeft(5);
        setIsAnswered(false);
      } else {
        setGameState('summary');
      }
    }, 1500);
  }, [isAnswered, questions, currentQuestionIndex, timeLeft]);

  // Timer countdown effect
  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0 && !isAnswered) {
      const timer = setTimeout(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !isAnswered) {
      // Time's up - mark as incorrect and move to next
      handleAnswer(-1); // -1 indicates no answer selected
    }
  }, [timeLeft, gameState, isAnswered, handleAnswer]);

  // Reset game
  const resetGame = () => {
    setGameState('start');
  };

  const currentQuestion = questions[currentQuestionIndex];
  const totalDiscount = Math.min(score * 10, 50); // 10% per correct answer, max 50%

  return (
    <div className="App">
      {gameState === 'start' && (
        <StartScreen onStartGame={startGame} />
      )}
      
      {gameState === 'playing' && currentQuestion && (
        <QuizScreen
          question={currentQuestion}
          questionNumber={currentQuestionIndex + 1}
          totalQuestions={questions.length}
          timeLeft={timeLeft}
          onAnswer={handleAnswer}
          isAnswered={isAnswered}
          userAnswers={userAnswers}
        />
      )}
      
      {gameState === 'summary' && (
        <SummaryScreen
          userAnswers={userAnswers}
          score={score}
          totalQuestions={questions.length}
          totalDiscount={totalDiscount}
          onPlayAgain={startGame}
          onReset={resetGame}
        />
      )}
    </div>
  );
}

export default App;
